import React, { useMemo, useState } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { Box } from "@mui/material";

// import { ReactionsInOrgData, images, audios } from "../../../../constants";
import TablePaginationSection from "../../../../common/TablePaginationSection";
import ImagePreviewModal from "../../../../common/ImagePreviewModal";
import AudioModal from "../../../../common/AudioModal";
import { TableRowSection } from "../components";
import { useReactionsContext } from "../../../../providers/OrganizationAdmin/ReactionsProvider";
import CommonTableHead from "../../../../common/CommonTableHead";
import {
  ReactionsInOrgTableHeadCells,
  getComparator,
  stableSort,
} from "../../../../constants";

export default function ReactionsTable({
  selected,
  setSelected,
  reactionsInOrg,
}) {
  const {
    order,
    setOrder,
    orderBy,
    setOrderBy,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
  } = useReactionsContext();

  const [imagePreviewModal, setImagePreviewModal] = useState(false);
  const [audioModal, setAudioModal] = useState(false);
  const [selectedAudioFile, setSelectedAudioFile] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);

  // Initialize column widths based on ReactionsInOrgTableHeadCells
  const initializeColumnWidths = () => {
    const widths = {};
    ReactionsInOrgTableHeadCells.forEach(cell => {
      widths[cell.id] = cell.width || 150;
    });
    return widths;
  };

  const [columnWidths, setColumnWidths] = useState(initializeColumnWidths);

  const handleColumnResize = (columnId, newWidth) => {
    setColumnWidths(prev => ({
      ...prev,
      [columnId]: newWidth
    }));
  };

  console.log("reactions:", reactionsInOrg);

  const handleImagePreviewModalOpen = () => {
    setImagePreviewModal(true);
  };
  const handleImagePreviewModalClose = () => {
    setImagePreviewModal(false);
    setSelectedImage(null);
  };

  const handleAudioModalOpen = () => {
    setAudioModal(true);
  };
  const handleAudioModalClose = () => {
    setAudioModal(false);
    setSelectedAudioFile(null);
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const isSelected = (id) => selected.indexOf(id) !== -1;

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page - 1 > 0
      ? Math.max(0, (1 + page - 1) * rowsPerPage - reactionsInOrg.length)
      : 0;

  const visibleRows = useMemo(
    () =>
      stableSort(
        reactionsInOrg.length > 0 ? reactionsInOrg : [],
        getComparator(order, orderBy)
      ).slice((page - 1) * rowsPerPage, (page - 1) * rowsPerPage + rowsPerPage),
    [order, orderBy, page, rowsPerPage, reactionsInOrg]
  );

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelected = visibleRows.map((n) => n.id);
      // const newSelected = reactionsInOrg.map((n) => n.id);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleClickRow = (event, id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }
    setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    console.log(newPage);
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  return (
    <Box sx={boxContainerStyles}>
      <Paper sx={paperContainerStyles}>
        <TableContainer
          style={{ border: "none", boxShadow: "none", maxHeight: "calc(90vh - 220px)", maxWidth: "calc(90vw - 120px)" }}
        >
          <Table
            stickyHeader
            aria-labelledby="tableTitle"
          >
            <CommonTableHead
              numSelected={selected.length}
              order={order}
              orderBy={orderBy}
              onSelectAllClick={handleSelectAllClick}
              onRequestSort={handleRequestSort}
              rowCount={visibleRows.length}
              tableHeadCells={ReactionsInOrgTableHeadCells}
              columnWidths={columnWidths}
              onColumnResize={handleColumnResize}
            />
            <TableBody>
              {visibleRows.map((row, index) => {
                const isItemSelected = isSelected(row.id);
                const labelId = `enhanced-table-checkbox-${index}`;
                const numberType = index % 2 == 0;

                return (
                  <TableRowSection
                    isItemSelected={isItemSelected}
                    row={row}
                    handleClickRow={handleClickRow}
                    numberType={numberType}
                    labelId={labelId}
                    key={index}
                    columnWidths={columnWidths}
                    setSelectedAudioFile={setSelectedAudioFile}
                    setSelectedImage={setSelectedImage}
                    handleImagePreviewModalOpen={handleImagePreviewModalOpen}
                    handleAudioModalOpen={handleAudioModalOpen}
                  />
                );
              })}
              {emptyRows > 0 && (
                <TableRow
                  style={{
                    height: 53 * emptyRows,
                  }}
                >
                  <TableCell colSpan={6} />
                </TableRow>
              )}
               {visibleRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    No results found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      <TablePaginationSection
        count={Math.ceil(reactionsInOrg.length / rowsPerPage)}
        page={page}
        total={reactionsInOrg.length ?? 0}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      <ImagePreviewModal
        modalOpen={imagePreviewModal}
        handleClose={handleImagePreviewModalClose}
        // image={images.tableDummyImage}
        image={selectedImage && selectedImage}
      />
      <AudioModal
        modalOpen={audioModal}
        handleClose={handleAudioModalClose}
        // audioFile={audios.audioSample}
        audioFile={selectedAudioFile && selectedAudioFile}
      />
    </Box>
  );
}

const boxContainerStyles = {
  width: "100%",
  paddingRight: 1,
  border: "none",
  boxShadow: "none",
  // marginTop: 2,
};

const paperContainerStyles = {
  width: "100%",
  mb: 1,
  border: "none",
  boxShadow: "none",
  mt: 1,
};
